import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Header } from '../components/shared/Header';
import { Footer } from '../components/landing/Footer';
import { Button } from '../components/shared/Button';
import { subscriptionService } from '../services/subscription.service';
import { useAuth } from '../contexts/AuthContext';
import { AlertCircle, CheckCircle2, Loader2, Mail } from 'lucide-react';

type ClaimStatus = 'claiming' | 'success' | 'alreadyClaimed' | 'error';

/**
 * Landing page for the anonymous pass purchase return. Stripe sends the buyer
 * here with `session_id`; the page exchanges it for a token pair exactly once
 * and then behaves as a normal login.
 */
export const PassSuccessPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adoptSession, isAuthenticated, loading } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState<ClaimStatus>('claiming');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const hasRun = useRef(false);

  useEffect(() => {
    // Wait until the auth state is known, so a logged-in buyer is not mistaken
    // for an anonymous one when the backend refuses to mint for a pre-existing
    // account.
    if (loading) return;
    if (hasRun.current) return;
    hasRun.current = true;

    if (!sessionId) {
      setStatus('error');
      setErrorMessage(t('passSuccess.missingSession'));
      return;
    }

    const claim = async () => {
      try {
        const data = await subscriptionService.claimPass(sessionId);
        await adoptSession(data);
        setStatus('success');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        const response = (err as { response?: { status?: number; data?: { code?: string; message?: string } } })
          ?.response;
        // The pass is granted either way; an existing account simply cannot be
        // handed a new session. A buyer who is already logged in just continues
        // to the dashboard, everyone else must log in / set a password.
        if (response?.data?.code === 'PASS_ACCOUNT_EXISTS') {
          if (isAuthenticated) {
            navigate('/dashboard', { replace: true });
            return;
          }
          setStatus('alreadyClaimed');
          return;
        }
        if (response?.status === 409 || response?.data?.code === 'PASS_SESSION_ALREADY_CLAIMED') {
          setStatus('alreadyClaimed');
          return;
        }
        setStatus('error');
        setErrorMessage(response?.data?.message || t('passSuccess.errorBody'));
      }
    };

    void claim();
  }, [sessionId, loading, isAuthenticated, adoptSession, navigate, t]);

  return (
    <div className="page-shell">
      <Header />

      <main className="mx-auto max-w-2xl px-4 py-16 sm:px-6 md:py-24">
        <div className="rounded-3xl border border-primary/10 bg-white p-8 text-center shadow-surface md:p-12">
          {status === 'claiming' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <p className="text-slate-600">{t('passSuccess.claiming')}</p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <CheckCircle2 className="h-12 w-12 text-success" />
              <p className="text-slate-600">{t('passSuccess.success')}</p>
            </div>
          )}

          {status === 'alreadyClaimed' && (
            <div className="space-y-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-50">
                <Mail className="h-7 w-7 text-amber-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-ink">{t('passSuccess.alreadyClaimedTitle')}</h1>
                <p className="mt-2 text-slate-600">{t('passSuccess.alreadyClaimedBody')}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Link to="/login">
                  <Button variant="primary" className="w-full sm:w-auto">
                    {t('passSuccess.loginCta')}
                  </Button>
                </Link>
                <Link to="/reset-password">
                  <Button variant="secondary" className="w-full sm:w-auto">
                    {t('passSuccess.setPasswordCta')}
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-6">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-error/10">
                <AlertCircle className="h-7 w-7 text-error" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-ink">{t('passSuccess.errorTitle')}</h1>
                <p className="mt-2 text-slate-600">{errorMessage || t('passSuccess.errorBody')}</p>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
                <Link to="/login">
                  <Button variant="primary" className="w-full sm:w-auto">
                    {t('passSuccess.loginCta')}
                  </Button>
                </Link>
                <Link to="/reset-password">
                  <Button variant="secondary" className="w-full sm:w-auto">
                    {t('passSuccess.setPasswordCta')}
                  </Button>
                </Link>
              </div>
              <p className="text-sm text-slate-500">
                {t('passSuccess.contactSupport')}{' '}
                <Link to="/contact" className="text-primary underline hover:no-underline">
                  {t('contact.title')}
                </Link>
              </p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PassSuccessPage;
